import {RefObject, useLayoutEffect, useState} from 'react';

/**
 * A hook that tells whether a single-line element is clipped by `text-overflow: ellipsis`,
 * so a caller can offer the full text in a tooltip only where the label is really cut off.
 * Re-measures whenever the element resizes, because the available width changes with the
 * surrounding layout.
 *
 * @param elementRef A ref attached to the element being measured
 * @param dependency A value that re-triggers the measurement when the rendered text changes
 *
 * @example
 * ```tsx
 * const textRef = useRef<HTMLDivElement>(null);
 * const isTruncated = useIsTruncated(textRef, label);
 * ```
 */
export function useIsTruncated(elementRef: RefObject<HTMLElement | null>, dependency: unknown) {
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!element) {
      return;
    }

    const checkTruncation = () => setIsTruncated(element.scrollWidth > element.clientWidth);

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);

    checkTruncation();

    return () => resizeObserver.disconnect();
  }, [elementRef, dependency]);

  return isTruncated;
}
